import React, { useEffect, useMemo, useRef, useState } from 'react';
import BrowserVoiceWidget from '../components/BrowserVoiceWidget';
import { getApiBaseUrl } from '../lib/apiConfig';

// Absolute Agent-server base (port 3016) — the /api/voices and /api/voice-lab/* endpoints live
// there. In production the frontend is a different origin, so a relative /api path 404s to HTML.
const API = getApiBaseUrl();

/*
 * Voice Lab — internal tester for STT / TTS / voice combinations.
 * Separate page (route /voice-lab), gated by a simple client-side password.
 * NOTE: client-side gate — the password ships in the bundle. Low-stakes internal use only.
 * Set REACT_APP_VOICELAB_USER / REACT_APP_VOICELAB_PASSWORD at build time to change credentials.
 */

const LAB_USER = process.env.REACT_APP_VOICELAB_USER || 'admin';
const LAB_PASS = process.env.REACT_APP_VOICELAB_PASSWORD || 'freshvoice';
const UNLOCK_KEY = 'voicelab_unlocked_v1';

const LANGS = [
  ['', 'Auto / default'], ['en', 'English'], ['zh', 'Chinese'], ['ja', 'Japanese'],
  ['ko', 'Korean'], ['id', 'Indonesian'], ['es', 'Spanish'], ['fr', 'French'],
  ['de', 'German'], ['hi', 'Hindi'], ['pt', 'Portuguese'], ['vi', 'Vietnamese'], ['th', 'Thai'],
];
const STT_PROVIDERS = [
  ['openai', 'OpenAI (gpt-4o-transcribe / whisper)'],
  ['deepgram', 'Deepgram (nova)'],
];
const STT_MODELS = {
  openai: ['gpt-4o-transcribe', 'gpt-4o-mini-transcribe', 'whisper-1'],
  deepgram: ['nova-2', 'nova-3', 'nova-2-phonecall'],
};

// TTS providers across companies. Cartesia/ElevenLabs/Kokoro pull voices from /api/voices;
// OpenAI and Deepgram (Aura) use fixed voice lists.
const TTS_PROVIDERS = [
  ['cartesia', 'Cartesia (Sonic)'],
  ['elevenlabs', 'ElevenLabs'],
  ['openai', 'OpenAI'],
  ['hume', 'Hume (Octave — emotional)'],
  ['deepgram', 'Deepgram (Aura)'],
  ['fish', 'Fish Audio (clone / library)'],
  ['kokoro', 'Kokoro (self-hosted)'],
];
const TTS_STATIC_VOICES = {
  openai: ['alloy', 'ash', 'ballad', 'coral', 'echo', 'fable', 'onyx', 'nova', 'sage', 'shimmer', 'verse', 'marin', 'cedar'],
  deepgram: [
    // Aura-2 (newer, higher quality)
    'aura-2-thalia-en', 'aura-2-andromeda-en', 'aura-2-helena-en', 'aura-2-apollo-en',
    'aura-2-arcas-en', 'aura-2-aries-en', 'aura-2-amalthea-en', 'aura-2-atlas-en',
    'aura-2-aurora-en', 'aura-2-callista-en', 'aura-2-cora-en', 'aura-2-cordelia-en',
    'aura-2-delia-en', 'aura-2-draco-en', 'aura-2-electra-en', 'aura-2-harmonia-en',
    'aura-2-hera-en', 'aura-2-hermes-en', 'aura-2-hyperion-en', 'aura-2-iris-en',
    'aura-2-janus-en', 'aura-2-juno-en', 'aura-2-jupiter-en', 'aura-2-luna-en',
    'aura-2-mars-en', 'aura-2-minerva-en', 'aura-2-neptune-en', 'aura-2-odysseus-en',
    'aura-2-ophelia-en', 'aura-2-orion-en', 'aura-2-orpheus-en', 'aura-2-pandora-en',
    'aura-2-phoebe-en', 'aura-2-saturn-en', 'aura-2-selene-en', 'aura-2-theia-en',
    'aura-2-vesta-en', 'aura-2-zeus-en',
    // Aura-1 (original)
    'aura-asteria-en', 'aura-luna-en', 'aura-stella-en', 'aura-athena-en', 'aura-hera-en',
    'aura-orion-en', 'aura-arcas-en', 'aura-perseus-en', 'aura-angus-en', 'aura-orpheus-en',
    'aura-helios-en', 'aura-zeus-en',
  ],
};
const TTS_MODELS = {
  openai: ['gpt-4o-mini-tts', 'tts-1', 'tts-1-hd'],
  cartesia: ['', 'sonic-3.6', 'sonic-2'],
  elevenlabs: ['', 'eleven_v3', 'eleven_flash_v2_5', 'eleven_multilingual_v2', 'eleven_turbo_v2_5'],
  fish: ['s2.1-pro-free', 's2.1-pro', 's1', 's2-pro', 'drama-3-preview'],
};
// Providers whose tone is set by a natural-language instruction ("say this sadly").
const TONE_PROVIDERS = new Set(['openai', 'hume']);
const CARTESIA_EMOTIONS = ['neutral', 'happy', 'excited', 'sad', 'angry', 'curious', 'calm'];
const TTS_SPEEDS = ['', 'slowest', 'slow', 'normal', 'fast', 'fastest'];
// ElevenLabs v3 audio tags — emotions + non-verbal "signs", chosen from a dropdown → [tag].
const ELEVEN_TAGS = ['', 'happy', 'sad', 'angry', 'excited', 'whispering', 'calm', 'nervous',
  'sarcastic', 'curious', 'laughs', 'chuckles', 'sighs', 'exhales', 'breathes', 'gasps',
  'clears throat', 'sniffs', 'gulps'];
// Filler words — plain text, so they work on every provider. Prepended before the line.
const FILLERS = ['', 'Ok', 'Okay', 'Um', 'Uh', 'Hmm', 'Well', 'So', 'Right', 'You know', 'Actually', 'Let me see'];

const card = 'rounded-2xl bg-white ring-1 ring-slate-900/5 shadow-sm';
const label = 'block text-[12px] font-semibold text-slate-500 mb-1';
const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-teal-500';
const btn = 'inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-[14px] font-semibold text-white hover:bg-teal-700 disabled:opacity-50';
const btnGhost = 'inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-[14px] font-semibold text-teal-700 ring-1 ring-slate-300 hover:bg-teal-50';

// ---- Animated orb + glyphs (matches the web-call widget's look) ----
const MicGlyph = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
const SpeakerGlyph = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 5 6 9H2v6h4l5 4V5z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M19 5a9 9 0 0 1 0 14" />
  </svg>
);
// Pulsing circular button — idle slate, active teal (ping+pulse rings), danger rose.
function Orb({ active, danger, onClick, disabled, size = 120, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label="orb"
      className="relative flex items-center justify-center rounded-full outline-none disabled:opacity-60"
      style={{ height: size, width: size }}>
      {active && <span className="absolute inset-0 animate-ping rounded-full bg-teal-400/30" />}
      {active && <span className="absolute inset-1 animate-pulse rounded-full bg-teal-400/25" />}
      <span className={`relative flex items-center justify-center rounded-full text-white shadow-lg transition-colors ${
        danger ? 'bg-rose-500' : active ? 'bg-teal-600' : 'bg-slate-300 hover:bg-slate-400'}`}
        style={{ height: size * 0.84, width: size * 0.84 }}>
        {children}
      </span>
    </button>
  );
}
function StatusPill({ active, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[12.5px] font-medium text-slate-600 ring-1 ring-slate-900/5 shadow-sm">
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-teal-500 animate-pulse' : 'bg-slate-300'}`} />
      {label}
    </span>
  );
}
// Grid of metric tiles (last one highlighted). items = [[label, value, unit?], …]
function StatTiles({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {items.map(([k, v, unit], i) => (
        <div key={k} className={`rounded-xl px-3 py-2 ${i === items.length - 1 ? 'bg-teal-50' : 'bg-slate-50'}`}>
          <div className={`text-[10px] font-semibold uppercase tracking-wide ${i === items.length - 1 ? 'text-teal-600' : 'text-slate-400'}`}>{k}</div>
          <div className={`text-[15px] font-bold ${i === items.length - 1 ? 'text-teal-700' : 'text-slate-700'}`}>
            {v == null || v === '' ? '—' : v}{unit && <span className="text-[10px] font-medium text-slate-400"> {unit}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Login({ onOk }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (u === LAB_USER && p === LAB_PASS) {
      try { sessionStorage.setItem(UNLOCK_KEY, '1'); } catch (_) {}
      onOk();
    } else setErr('Wrong username or password.');
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form onSubmit={submit} className={`${card} w-full max-w-sm p-7`}>
        <h1 className="text-[20px] font-bold text-slate-900">Voice Lab</h1>
        <p className="mt-1 text-[13px] text-slate-500">Internal STT / TTS tester. Please sign in.</p>
        <div className="mt-5 space-y-3">
          <div><label className={label}>Username</label><input className={input} value={u} onChange={(e) => setU(e.target.value)} autoFocus /></div>
          <div><label className={label}>Password</label><input className={input} type="password" value={p} onChange={(e) => setP(e.target.value)} /></div>
        </div>
        {err && <p className="mt-3 text-[13px] text-red-600">{err}</p>}
        <button className={`${btn} mt-5 w-full`} type="submit">Enter</button>
      </form>
    </div>
  );
}

// Many catalog voices ship without a `provider` field (the curated Cartesia list and every
// Kokoro voice), so infer it from the id: a UUID is Cartesia, an "xx_name" id is Kokoro.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function inferProvider(v, id) {
  const p = (v.provider || v.engine || '').toLowerCase();
  if (p) return p.includes('eleven') ? 'elevenlabs' : p.includes('fish') ? 'fish' : p;
  if (typeof id === 'string' && id.startsWith('fish:')) return 'fish';
  if (UUID.test(id)) return 'cartesia';
  if (/^[a-z]{2}_/.test(id)) return 'kokoro';
  return 'other';
}

function useVoices() {
  const [voices, setVoices] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    fetch(`${API}/api/voices`).then((r) => r.json()).then((d) => {
      const list = (d.voices || []).map((v) => {
        const id = v.id || v.voiceId || v.voice_id || '';
        return { id, name: v.name || v.label || id || 'Voice', provider: inferProvider(v, id), language: v.language || v.lang || '' };
      }).filter((v) => v.id);
      setVoices(list);
      if (!list.length) setErr('No voices returned by /api/voices — check provider keys on the server.');
    }).catch((e) => setErr('Failed to load /api/voices: ' + e.message));
  }, []);
  return [voices, err];
}

// Best-effort list of the signed-in user's scripts (uses the dashboard token if present).
function useScripts() {
  const [scripts, setScripts] = useState([]);
  useEffect(() => {
    let token = null;
    try { token = localStorage.getItem('token'); } catch (_) {}
    fetch(`${API}/api/scripts`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const list = Array.isArray(d) ? d : (d.scripts || d.data || []);
        setScripts(list.map((s) => ({ id: String(s._id || s.id || ''), name: s.scriptName || s.name || s.title || String(s._id || s.id) })).filter((s) => s.id));
      })
      .catch(() => setScripts([]));
  }, []);
  return scripts;
}

// Searchable, scrollable dropdown. options = [{ value, label, group? }].
function SearchableSelect({ options, value, onChange, placeholder = '— select —' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = q
    ? options.filter((o) => `${o.label} ${o.group || ''}`.toLowerCase().includes(q.toLowerCase()))
    : options;
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${input} flex items-center justify-between text-left`}>
        <span className={selected ? 'truncate' : 'text-slate-400'}>{selected ? selected.label : placeholder}</span>
        <span className="ml-2 shrink-0 text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-full border-b border-slate-100 px-3 py-2 text-[13px] focus:outline-none" />
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && <div className="px-3 py-2 text-[13px] text-slate-400">No matches</div>}
            {filtered.map((o) => (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); setQ(''); }}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-[13.5px] hover:bg-teal-50 ${String(o.value) === String(value) ? 'text-teal-700 font-semibold' : 'text-slate-700'}`}>
                <span className="truncate">{o.label}</span>
                {o.group && <span className="ml-2 shrink-0 text-[11px] uppercase tracking-wide text-slate-400">{o.group}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VoiceSelect({ voices, value, onChange }) {
  const options = useMemo(
    () => voices.map((v) => ({ value: v.id, label: `${v.name}${v.language ? ` (${v.language})` : ''}`, group: v.provider })),
    [voices]
  );
  return <SearchableSelect options={options} value={value} onChange={onChange} placeholder="— pick a voice —" />;
}

/* ---------------- TTS playground ---------------- */
function TtsTab({ voices }) {
  const [text, setText] = useState('Hello, thank you for calling. How can I help you today?');
  const [provider, setProvider] = useState('cartesia');
  const [voiceId, setVoiceId] = useState('');
  const [language, setLanguage] = useState('en');
  const [model, setModel] = useState('');
  const [instructions, setInstructions] = useState('');
  const [emotion, setEmotion] = useState('neutral');
  const [speed, setSpeed] = useState('');
  const [elevenTag, setElevenTag] = useState('');
  const [voiceDesc, setVoiceDesc] = useState('');
  const [filler, setFiller] = useState('');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [playing, setPlaying] = useState(false);
  const [audioDur, setAudioDur] = useState(null);
  const audioRef = useRef(null);
  const [err, setErr] = useState('');

  // Auto-play the newest synthesis; the orb pulses while it plays.
  useEffect(() => { if (audioUrl && audioRef.current) audioRef.current.play().catch(() => {}); }, [audioUrl]);

  const catalogVoices = useMemo(
    () => voices.filter((v) => v.provider === provider),
    [voices, provider]
  );
  const isHume = provider === 'hume';
  const staticVoices = TTS_STATIC_VOICES[provider];
  const models = TTS_MODELS[provider];

  // Reset voice/model when provider changes.
  useEffect(() => {
    if (isHume) setVoiceId('');
    else if (staticVoices) setVoiceId(staticVoices[0]);
    else setVoiceId(catalogVoices[0]?.id || '');
    setModel(models ? models[0] : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, voices]);

  const speak = async () => {
    setErr(''); setInfo(null); setAudioDur(null); setBusy(true);
    const t0 = Date.now();
    let outText = filler ? `${filler}, ${text}` : text;
    if (provider === 'elevenlabs' && elevenTag) outText = `[${elevenTag}] ${outText}`;
    const chars = outText.length;
    try {
      const r = await fetch(`${API}/api/voice-lab/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: outText,
          provider, voiceId: voiceId || undefined, language: language || undefined, model: model || undefined,
          instructions: TONE_PROVIDERS.has(provider) ? (instructions || undefined) : undefined,
          emotion: provider === 'cartesia' && emotion !== 'neutral' ? emotion : undefined,
          speed: provider === 'cartesia' ? (speed || undefined) : undefined,
          voiceDescription: provider === 'elevenlabs' ? (voiceDesc || undefined) : undefined,
        }),
      });
      const d = await r.json();
      if (!d.success || !d.audio) throw new Error(d.message || 'No audio');
      setAudioUrl(`data:${d.format === 'mp3' ? 'audio/mpeg' : 'audio/wav'};base64,${d.audio}`);
      const ms = d.latencyMs || (Date.now() - t0);
      const bytes = Math.round((d.audio.length * 3) / 4);
      setInfo({ provider: d.provider, model: d.model, sampleRate: d.sampleRate, ms, chars, bytes, filler: filler || (provider === 'elevenlabs' && elevenTag ? `[${elevenTag}]` : '') });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className={`${card} p-6 space-y-4`}>
      <div>
        <label className={label}>Text to speak</label>
        <textarea className={`${input} h-24`} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div><label className={label}>Provider (company)</label>
          <select className={input} value={provider} onChange={(e) => setProvider(e.target.value)}>
            {TTS_PROVIDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div><label className={label}>Voice {isHume ? '(optional)' : ''}</label>
          {isHume
            ? <input className={input} value={voiceId} onChange={(e) => setVoiceId(e.target.value)} placeholder="blank = from description" />
            : staticVoices
            ? <SearchableSelect options={staticVoices.map((v) => ({ value: v, label: v }))} value={voiceId} onChange={setVoiceId} placeholder="— pick a voice —" />
            : <SearchableSelect options={catalogVoices.map((v) => ({ value: v.id, label: `${v.name}${v.language ? ` (${v.language})` : ''}` }))} value={voiceId} onChange={setVoiceId} placeholder="— pick a voice —" />}
        </div>
        {models && (
          <div><label className={label}>Model</label>
            <select className={input} value={model} onChange={(e) => setModel(e.target.value)}>{models.map((m) => <option key={m} value={m}>{m || 'provider default'}</option>)}</select>
          </div>
        )}
        <div><label className={label}>Language</label>
          <select className={input} value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {/* Filler word — works on any provider (it's just text) */}
      <div className="grid gap-4 md:grid-cols-2">
        <div><label className={label}>Filler word <span className="text-slate-400">(prepended to the line)</span></label>
          <select className={input} value={filler} onChange={(e) => setFiller(e.target.value)}>
            {FILLERS.map((f) => <option key={f} value={f}>{f || 'none'}</option>)}
          </select>
        </div>
        {filler && <p className="text-[12px] text-slate-400 self-end pb-2">Speaks: <span className="text-slate-600">“{filler}, {text.slice(0, 40)}{text.length > 40 ? '…' : ''}”</span></p>}
      </div>

      {/* Tone / emotion controls */}
      {TONE_PROVIDERS.has(provider) && (
        <div>
          <label className={label}>Tone / instructions <span className="text-slate-400">(natural language — "say this sadly", "excited and fast, almost whispering")</span></label>
          <input className={input} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder='e.g. "Speak in a warm, sympathetic, slow tone"' />
          {provider === 'openai' && (
            <p className="mt-1 text-[12px] text-slate-400">OpenAI takes tone from this instruction, on model <b>gpt-4o-mini-tts</b> only. It does <b>not</b> use <code>[bracket]</code> audio tags — those are ElevenLabs (use the ElevenLabs provider + <b>eleven_v3</b> for tags).</p>
          )}
        </div>
      )}
      {provider === 'cartesia' && (
        <div className="grid gap-4 md:grid-cols-2">
          <div><label className={label}>Emotion</label>
            <select className={input} value={emotion} onChange={(e) => setEmotion(e.target.value)}>{CARTESIA_EMOTIONS.map((m) => <option key={m} value={m}>{m}</option>)}</select>
          </div>
          <div><label className={label}>Speed</label>
            <select className={input} value={speed} onChange={(e) => setSpeed(e.target.value)}>{TTS_SPEEDS.map((s) => <option key={s} value={s}>{s || 'normal (default)'}</option>)}</select>
          </div>
        </div>
      )}
      {provider === 'elevenlabs' && (
        <div className="space-y-3">
          <div className="grid gap-4 md:grid-cols-2">
            <div><label className={label}>Emotion / tag</label>
              <select className={input} value={elevenTag} onChange={(e) => setElevenTag(e.target.value)}>
                {ELEVEN_TAGS.map((t) => <option key={t} value={t}>{t ? `[${t}]` : 'none'}</option>)}
              </select>
            </div>
            <p className="text-[12px] text-slate-400 self-end pb-2">Prepended to the text as <code>[{elevenTag || 'tag'}]</code>. Best with model <b>eleven_v3</b>; you can still type more tags inline.</p>
          </div>
          <div>
            <label className={label}>Voice from description <span className="text-slate-400">(Voice Design — generates a new voice from your prompt; overrides the picked voice)</span></label>
            <input className={input} value={voiceDesc} onChange={(e) => setVoiceDesc(e.target.value)} placeholder='e.g. "a warm, older British man, calm and reassuring, slightly gravelly"' />
          </div>
        </div>
      )}
      {provider === 'fish' && (
        <div>
          <label className={label}>Or paste a fish.audio model ID <span className="text-slate-400">(from a model's URL, e.g. fish.audio/m/<b>802e3bc2…</b> — overrides the picked voice)</span></label>
          <input className={input} value={voiceId} onChange={(e) => setVoiceId(e.target.value)} placeholder="802e3bc2b27e49c2995d23ef70e6ac89" />
        </div>
      )}
      {(provider === 'deepgram' || provider === 'kokoro') && (
        <p className="text-[12px] text-slate-400">Fixed voices — no tone/emotion prompt on this provider. Pick a different voice above to change the sound.</p>
      )}

      <div className="flex flex-col items-center gap-3 pt-2">
        <Orb active={playing} onClick={() => audioRef.current && audioRef.current.play()} disabled={!audioUrl}>
          <SpeakerGlyph size={42} />
        </Orb>
        <StatusPill active={playing || busy} label={busy ? 'Synthesizing…' : playing ? 'Speaking…' : audioUrl ? 'Tap the orb to replay' : 'Ready'} />
        <button className={`${btn} px-6`} onClick={speak} disabled={busy || (!voiceId && !isHume && !(provider === 'elevenlabs' && voiceDesc)) || !text.trim()}>{busy ? 'Synthesizing…' : '▶ Speak'}</button>
        {audioUrl && <audio ref={audioRef} src={audioUrl} onLoadedMetadata={(e) => setAudioDur(e.target.duration)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} controls className="h-9 w-full max-w-sm" />}
        {err && <span className="text-[13px] text-red-600">{err}</span>}
      </div>
      {info && (
        <div className="space-y-2">
          <StatTiles items={[
            ['Synth (round-trip)', info.ms, 'ms'],
            ['Audio length', audioDur ? audioDur.toFixed(2) : '…', 's'],
            ['Chars', info.chars],
            ['Chars/sec', audioDur ? Math.round(info.chars / audioDur) : '…'],
            ['Faster than real-time', audioDur ? `${(audioDur / (info.ms / 1000)).toFixed(1)}×` : '…'],
            [info.provider, `${info.sampleRate / 1000}kHz`],
          ]} />
          {info.filler && <p className="text-[12px] text-slate-400">Prefix added: <span className="text-slate-600">{info.filler}</span> · spoke {info.chars} chars ({(info.bytes / 1024).toFixed(0)} KB WAV)</p>}
        </div>
      )}
      <p className="text-[12px] text-slate-400">Compare companies on the same line. Mixed scripts (English + 中文 + 日本語) test per-language pronunciation on Cartesia.</p>
    </div>
  );
}

/* ---------------- STT playground ---------------- */
function SttTab() {
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o-transcribe');
  const [language, setLanguage] = useState('');
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recStartRef = useRef(0);
  const [clipDur, setClipDur] = useState(null);

  useEffect(() => { setModel(STT_MODELS[provider][0]); }, [provider]);

  const start = async () => {
    setErr(''); setResult(null); setClipDur(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setClipDur((Date.now() - recStartRef.current) / 1000);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        await transcribe(blob);
      };
      recStartRef.current = Date.now();
      recRef.current = mr; mr.start(); setRecording(true);
    } catch (e) { setErr('Mic error: ' + e.message); }
  };
  const stop = () => { try { recRef.current?.stop(); } catch (_) {} setRecording(false); };

  const transcribe = async (blob) => {
    setBusy(true);
    try {
      const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
      const r = await fetch(`${API}/api/voice-lab/stt`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio: b64, mimeType: blob.type, provider, model, language: language || undefined }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || (d.detail && JSON.stringify(d.detail)) || 'STT failed');
      setResult(d);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className={`${card} p-6 space-y-4`}>
      <div className="grid gap-4 md:grid-cols-3">
        <div><label className={label}>Provider</label>
          <select className={input} value={provider} onChange={(e) => setProvider(e.target.value)}>
            {STT_PROVIDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div><label className={label}>Model</label>
          <select className={input} value={model} onChange={(e) => setModel(e.target.value)}>
            {STT_MODELS[provider].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div><label className={label}>Language hint</label>
          <select className={input} value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="flex flex-col items-center gap-3 pt-2">
        <Orb active={recording} danger={recording} onClick={recording ? stop : start} disabled={busy && !recording}>
          <MicGlyph size={42} />
        </Orb>
        <StatusPill active={recording || busy} label={recording ? 'Recording — tap to stop' : busy ? 'Transcribing…' : 'Tap the mic to record'} />
        {err && <span className="text-[13px] text-red-600">{err}</span>}
      </div>
      {result && (
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-[12px] text-slate-500">{result.provider} · {result.model}</p>
            <p className="mt-1 text-[15px] text-slate-900">{result.transcript || <span className="text-slate-400">(empty)</span>}</p>
          </div>
          {(() => {
            const words = (result.transcript || '').trim().split(/\s+/).filter(Boolean).length;
            return <StatTiles items={[
              ['Transcribe', result.latencyMs, 'ms'],
              ['Clip length', clipDur ? clipDur.toFixed(2) : '…', 's'],
              ['Words', words],
              ['Chars', (result.transcript || '').length],
              ['Words/sec', clipDur ? (words / clipDur).toFixed(1) : '…'],
              ['Faster than real-time', clipDur ? `${(clipDur / (result.latencyMs / 1000)).toFixed(1)}×` : '…'],
            ]} />;
          })()}
        </div>
      )}
      <p className="text-[12px] text-slate-400">Record a phrase, then stop — the clip is sent once and transcribed. Speak any language to test recognition.</p>
    </div>
  );
}

/* ---------------- Live combo call ---------------- */
function LiveTab({ voices }) {
  const [voiceId, setVoiceId] = useState('');
  const [ttsLanguage, setTtsLanguage] = useState('en');
  const [ttsModel, setTtsModel] = useState('');
  const [sttModel, setSttModel] = useState('nova-2');
  const [sttLanguage, setSttLanguage] = useState('');
  const [fillers, setFillers] = useState(false);
  const [running, setRunning] = useState(0); // key to (re)mount the widget
  const [m, setM] = useState({ turns: 0, total: 0, last: null });
  const [heard, setHeard] = useState('');
  const [speakingText, setSpeakingText] = useState('');

  const ttsProvider = useMemo(() => (voices.find((x) => x.id === voiceId) || {}).provider || '', [voices, voiceId]);
  const ttsModels = TTS_MODELS[ttsProvider];
  // Reset the model when the chosen voice's provider changes.
  useEffect(() => { setTtsModel(ttsModels ? ttsModels[0] : ''); /* eslint-disable-next-line */ }, [ttsProvider]);

  const cfg = useMemo(() => ({
    language: ttsLanguage || 'en',
    fillers,
    fillerMode: 'ai',
    voiceCfg: { sttModel, sttLanguage: sttLanguage || undefined, ttsModel: ttsModel || undefined },
  }), [ttsLanguage, sttModel, sttLanguage, ttsModel, fillers]);

  const onMetrics = (lat) => { if (lat) setM((p) => ({ turns: p.turns + 1, total: p.total + (lat.total || 0), last: lat })); };
  const onTranscript = (role, text) => {
    const s = typeof text === 'string' ? text : (text?.text ?? '');
    if (role === 'user') setHeard(s);
    else if (role === 'assistant') { setSpeakingText(s); setHeard(''); }
  };

  return (
    <div className="space-y-4">
      <div className={`${card} p-6`}>
        <div className="grid gap-4 md:grid-cols-3">
          <div><label className={label}>TTS voice</label><VoiceSelect voices={voices} value={voiceId} onChange={setVoiceId} /></div>
          <div><label className={label}>TTS language (start)</label>
            <select className={input} value={ttsLanguage} onChange={(e) => setTtsLanguage(e.target.value)}>{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div><label className={label}>TTS model</label>
            {ttsModels
              ? <select className={input} value={ttsModel} onChange={(e) => setTtsModel(e.target.value)}>{ttsModels.map((m2) => <option key={m2} value={m2}>{m2 || 'provider default'}</option>)}</select>
              : <input className={input} value="" disabled placeholder="provider default" />}
          </div>
          <div><label className={label}>STT model (Deepgram)</label>
            <select className={input} value={sttModel} onChange={(e) => setSttModel(e.target.value)}>{STT_MODELS.deepgram.map((m2) => <option key={m2} value={m2}>{m2}</option>)}</select>
          </div>
          <div><label className={label}>STT language</label>
            <select className={input} value={sttLanguage} onChange={(e) => setSttLanguage(e.target.value)}>{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div className="flex items-end"><button className={btnGhost} onClick={() => { setM({ turns: 0, total: 0, last: null }); setRunning((k) => k + 1); }}>{running ? 'Restart with this combo' : 'Start live call'}</button></div>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={fillers} onChange={(e) => setFillers(e.target.checked)} className="h-4 w-4 accent-teal-600" />
          AI-placed fillers (um / well / hmm — in context, ~1 in 3 sentences)
        </label>
        <p className="mt-3 text-[12px] text-slate-400">Live in-browser call uses Deepgram for STT + your chosen TTS voice/model. (OpenAI STT lives in the STT tab.) It's a generic assistant — no script needed.</p>
        {m.turns > 0 && (
          <div className="mt-5"><StatTiles items={[
            ['Turns', m.turns],
            ['STT', m.last?.detection ?? m.last?.stt, 'ms'],
            ['LLM', m.last?.gpt, 'ms'],
            ['TTS', m.last?.tts, 'ms'],
            ['Last round-trip (WS)', m.last?.total, 'ms'],
            ['Avg round-trip', m.turns ? Math.round(m.total / m.turns) : 0, 'ms'],
          ]} /></div>
        )}
      </div>
      {running > 0 && (
        <>
          <div className="rounded-2xl bg-slate-900 px-5 py-4 text-white shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-teal-300">Heard in realtime (STT)</div>
            <div className="mt-1 min-h-[24px] text-[15px] leading-snug">{heard ? heard : <span className="text-slate-500">…listening</span>}</div>
            {speakingText && <div className="mt-2 border-t border-white/10 pt-2 text-[13px] text-slate-300"><span className="text-teal-300">AI:</span> {speakingText}</div>}
          </div>
          <div className={`${card} p-4`}>
            <BrowserVoiceWidget key={running} backendMode="legacy" voiceId={voiceId || undefined} extraStart={cfg} onMetrics={onMetrics} onTranscript={onTranscript} showMetrics showTranscript />
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Conversation (generic AI voice chatbot) ---------------- */
function ChatTab({ voices }) {
  const [voiceId, setVoiceId] = useState('');
  const [ttsLanguage, setTtsLanguage] = useState('en');
  const [ttsModel, setTtsModel] = useState('');
  const [sttModel, setSttModel] = useState('nova-2');
  const [sttLanguage, setSttLanguage] = useState('');
  const [scriptId, setScriptId] = useState('');
  const [fillers, setFillers] = useState(false);
  const [running, setRunning] = useState(0);
  const [m, setM] = useState({ turns: 0, total: 0, last: null });
  const [heard, setHeard] = useState('');
  const [speakingText, setSpeakingText] = useState('');
  const scripts = useScripts();

  const ttsProvider = useMemo(() => (voices.find((x) => x.id === voiceId) || {}).provider || '', [voices, voiceId]);
  const ttsModels = TTS_MODELS[ttsProvider];
  useEffect(() => { setTtsModel(ttsModels ? ttsModels[0] : ''); /* eslint-disable-next-line */ }, [ttsProvider]);

  const cfg = useMemo(() => ({
    language: ttsLanguage || 'en',
    fillers,
    fillerMode: 'ai',
    voiceCfg: { sttModel, sttLanguage: sttLanguage || undefined, ttsModel: ttsModel || undefined },
  }), [ttsLanguage, sttModel, sttLanguage, ttsModel, fillers]);

  const onMetrics = (lat) => {
    if (!lat) return;
    setM((p) => ({ turns: p.turns + 1, total: p.total + (lat.total || 0), last: lat }));
  };
  // Realtime transcript — user partials arrive as they speak; the assistant line as it replies.
  const onTranscript = (role, text) => {
    const s = typeof text === 'string' ? text : (text?.text ?? '');
    if (role === 'user') setHeard(s);
    else if (role === 'assistant') { setSpeakingText(s); setHeard(''); }
  };

  return (
    <div className="space-y-4">
      <div className={`${card} p-6`}>
        <p className="mb-4 text-[13px] text-slate-500">A generic AI assistant — no topic, chat about anything (like ChatGPT voice). Or pick one of your scripts to run its real flow. Set the voice pipeline, then talk.</p>
        <div className="mb-4 grid items-end gap-4 md:grid-cols-2">
          <div><label className={label}>Script <span className="text-slate-400">(optional — runs the real flow instead of generic chat)</span></label>
            <SearchableSelect options={[{ value: '', label: 'Generic assistant (no script)' }, ...scripts.map((s) => ({ value: s.id, label: s.name }))]} value={scriptId} onChange={setScriptId} placeholder="Generic assistant (no script)" />
          </div>
          <label className="flex items-center gap-2 pb-2 text-[13.5px] font-medium text-slate-600">
            <input type="checkbox" checked={fillers} onChange={(e) => setFillers(e.target.checked)} className="h-4 w-4 accent-teal-600" />
            AI-placed fillers (um / well / hmm — in context, ~1 in 3 sentences)
          </label>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div><label className={label}>TTS voice</label><VoiceSelect voices={voices} value={voiceId} onChange={setVoiceId} /></div>
          <div><label className={label}>TTS model</label>
            {ttsModels
              ? <select className={input} value={ttsModel} onChange={(e) => setTtsModel(e.target.value)}>{ttsModels.map((mm) => <option key={mm} value={mm}>{mm || 'provider default'}</option>)}</select>
              : <input className={input} value="" disabled placeholder="provider default" />}
          </div>
          <div><label className={label}>TTS language (start)</label>
            <select className={input} value={ttsLanguage} onChange={(e) => setTtsLanguage(e.target.value)}>{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div><label className={label}>STT model (Deepgram)</label>
            <select className={input} value={sttModel} onChange={(e) => setSttModel(e.target.value)}>{STT_MODELS.deepgram.map((mm) => <option key={mm} value={mm}>{mm}</option>)}</select>
          </div>
          <div><label className={label}>STT language</label>
            <select className={input} value={sttLanguage} onChange={(e) => setSttLanguage(e.target.value)}>{LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div className="flex items-end"><button className={btn} onClick={() => { setM({ turns: 0, total: 0, last: null }); setRunning((k) => k + 1); }}>{running ? 'Restart conversation' : 'Start conversation'}</button></div>
        </div>
        {m.turns > 0 && (
          <div className="mt-5"><StatTiles items={[
            ['Turns', m.turns],
            ['STT', m.last?.detection ?? m.last?.stt, 'ms'],
            ['LLM', m.last?.gpt, 'ms'],
            ['TTS', m.last?.tts, 'ms'],
            ['Last round-trip (WS)', m.last?.total, 'ms'],
            ['Avg round-trip', m.turns ? Math.round(m.total / m.turns) : 0, 'ms'],
          ]} /></div>
        )}
      </div>
      {running > 0 && (
        <>
          <div className="rounded-2xl bg-slate-900 px-5 py-4 text-white shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-teal-300">Heard in realtime (STT)</div>
            <div className="mt-1 min-h-[24px] text-[15px] leading-snug">
              {heard ? heard : <span className="text-slate-500">…listening</span>}
            </div>
            {speakingText && <div className="mt-2 border-t border-white/10 pt-2 text-[13px] text-slate-300"><span className="text-teal-300">AI:</span> {speakingText}</div>}
          </div>
          <div className={`${card} p-4`}>
            <BrowserVoiceWidget key={running} backendMode="legacy" voiceId={voiceId || undefined} scriptId={scriptId || undefined} extraStart={cfg}
              systemPrompt="You are a helpful, friendly AI assistant. You can talk about anything the user brings up."
              firstMessage="Hi! I'm your AI assistant. What would you like to talk about?"
              agentName="AI Assistant" onMetrics={onMetrics} onTranscript={onTranscript} showMetrics showTranscript />
          </div>
        </>
      )}
    </div>
  );
}

export default function VoiceLab() {
  const [ok, setOk] = useState(() => { try { return sessionStorage.getItem(UNLOCK_KEY) === '1'; } catch (_) { return false; } });
  const [tab, setTab] = useState('tts');
  const [voices, voicesErr] = useVoices();

  if (!ok) return <Login onOk={() => setOk(true)} />;

  const tabs = [['chat', 'Conversation'], ['tts', 'TTS playground'], ['stt', 'STT playground'], ['live', 'Live combo call']];
  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 to-slate-50">
      <header className="border-b border-slate-200/70 bg-white/90 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto flex max-w-[1040px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white text-[15px] font-bold">V</span>
            <div>
              <h1 className="text-[17px] font-bold leading-tight text-slate-900">Voice Lab</h1>
              <p className="text-[11.5px] text-slate-400 leading-tight">internal STT · TTS · voice tester</p>
            </div>
          </div>
          <button className="text-[13px] font-medium text-slate-500 hover:text-slate-800" onClick={() => { try { sessionStorage.removeItem(UNLOCK_KEY); } catch (_) {} setOk(false); }}>Sign out</button>
        </div>
      </header>
      <main className="mx-auto max-w-[1040px] px-6 py-7">
        <div className="mb-5 inline-flex gap-1 rounded-xl bg-white p-1 ring-1 ring-slate-900/5 shadow-sm">
          {tabs.map(([id, name]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`rounded-lg px-4 py-2 text-[13.5px] font-semibold transition-colors ${tab === id ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-500 hover:text-teal-700'}`}>
              {name}
            </button>
          ))}
        </div>
        {voicesErr && <div className="mb-4 rounded-lg bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800 ring-1 ring-amber-200">{voicesErr}</div>}
        {tab === 'chat' && <ChatTab voices={voices} />}
        {tab === 'live' && <LiveTab voices={voices} />}
        {tab === 'tts' && <TtsTab voices={voices} />}
        {tab === 'stt' && <SttTab />}
      </main>
    </div>
  );
}
